import React, { useRef, useState } from 'react';

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
// Keep in lockstep with backend ReelConstants.AllowedVideoContentTypes (mp4 + quicktime).
const ACCEPT = 'video/mp4,video/quicktime';

export interface ReelUploadProps {
  onSelect: (file: File) => void;
  loading?: boolean;
  error?: string | null;
  success?: boolean;
  onRetry?: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Video file picker with 50MB client-side size check. Calls onSelect with the file (caller posts to POST /api/reels).
 */
const ReelUpload: React.FC<ReelUploadProps> = ({
  onSelect,
  loading = false,
  error: externalError = null,
  success = false,
  onRetry,
  disabled = false,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const error = externalError ?? sizeError;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSizeError(null);
    if (!file) return;
    if (file.size > MAX_SIZE_BYTES) {
      setSizeError('Video en fazla 50MB olabilir.');
      e.target.value = '';
      return;
    }
    setLastFile(file);
    onSelect(file);
    e.target.value = '';
  };

  const handleRetry = () => {
    if (lastFile && onRetry) {
      onRetry();
      onSelect(lastFile);
    }
  };

  const handleClick = () => {
    if (disabled || loading) return;
    inputRef.current?.click();
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleClick}
          disabled={disabled || loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Yükleniyor...
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt me-2" />
              Reel Yükle
            </>
          )}
        </button>
        {error && lastFile && onRetry && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleRetry}
            disabled={loading}
          >
            <i className="fas fa-redo me-2" />
            Tekrar Dene
          </button>
        )}
      </div>
      {error && (
        <div className="alert alert-danger small mt-2 mb-0" role="alert">
          <i className="fas fa-exclamation-circle me-2" />
          {error}
        </div>
      )}
      {success && !error && (
        <div className="alert alert-success small mt-2 mb-0" role="alert">
          <i className="fas fa-check-circle me-2" />
          Video başarıyla yüklendi!
        </div>
      )}
    </div>
  );
};

export default ReelUpload;
export { MAX_SIZE_BYTES };
